/**
 * 导入预览里的那张**账**（"我录了 100 个字，你收了多少？"）—— **唯一口径的纯函数**。
 *
 * ## 为什么必须有它（2026-10-16 用户原话）
 *
 * > "你保存之前，就是我录好了，保存之前你要能**预览**嘛。然后呢要**统计有多少个
 * >  字或者多少个词语**，那我会核对嘛。比如说我就录了 100 个字，结果呢你检测之后呢
 * >  只有 99 或者 101 这样的，那就跟我录的不一样，我就会去核对嘛，对不对？
 * >  **起码你数字要跟我录的核对之后对得上，我才会继续点保存**"
 *
 * 所以预览里要有一张**能自己加得起来**的账。这里把它的算法钉死成纯函数，
 * 目的只有一个：**对不上就是 bug**，不许用文案糊过去。
 *
 * ## 三条恒等式（`balanced` 是前两条的与）
 *
 * ```
 * tokens         = recognized + problem        // 每个词元要么收下、要么报错
 * recognized     = created + updated + skipped + merged + mergedPolyphonic + unresolved
 *                                              // 收下的每一条都有去向
 * distinctUnits  = created + updated + skipped + unresolved
 *                                              // 一共几个字/词（多音字只算一次）
 * ```
 *
 * ⚠️ 每一项的定义（含糊的话恒等式就没有意义）：
 *
 * | 项 | 含义 |
 * | --- | --- |
 * | `tokens` | 用户在框里粘的**词元数**（空格 / Tab / 换行切出来的 token 数） |
 * | `recognized` | 解析器收下的**数据行数**（一个字算一行、一个词算一行） |
 * | `problem` | **没收下**的词元数（字表里写了词、词语表里写了单个字） |
 * | `created` | 本次会**新增**的字/词条数 |
 * | `updated` | 已存在、但这次会**更新**（拼音/收录/词语表/年级）的条数 |
 * | `skipped` | 已存在、内容一致，**跳过**的条数 |
 * | `merged` | 同一份里重复、而且**这个字只有一个读音**（= 打错了）→ 合并掉的条数 |
 * | `mergedPolyphonic` | 同一份里重复、但那个字是**多音字**（有 2+ 读音）→ 只存 1 条、不算重复的条数 |
 * | `polyphonicChars` | 多音字**去重后**的条目数（"多音字有几个"，= 明细里那一组的长度） |
 * | `unresolved` | 收下了但**落不了库**的条数（内置字表没这个字、又没手工填拼音） |
 * | `distinctUnits` | **一共几个字/词（多音字只算一次）** = 实际会留在字表里的行数口径 |
 *
 * ⚠️ 为什么要单独一档 `mergedPolyphonic`（2026-10-17 用户原话）：
 *
 * > "有些是因为那个字是**多音字，所以重复出现了**，这种情况……**不能把它简单
 * >  当作重复的字处理**。"
 *
 * 所以"打错的重复"与"多音字的重复"必须**分开计数、分开说明**；但两者对库的影响
 * 相同（同一个字只存 1 行），所以两档都在这条恒等式里、都不进 `created/updated`。
 *
 * ⚠️ `distinctUnits` 是用户点名要的那个数：**一共几个字（多音字只算一次）**。
 *    它不参与 `balanced` 的判断（它由恒等式右边四项相加得到，天然成立），
 *    但它是"实际会入库多少行"的口径 —— 已存在而跳过的也在里面（它们本来就在库里）。
 *
 * ⚠️ `unresolved` 是真实存在的一档，少了它恒等式就会在"粘了一个生僻字"时
 *    对不上（服务层会为它单独报一条错，但那条错不是"解析时的词元违规"）。
 *    界面上把这一项**明写出来**（而不是把它算进"有问题"里含糊掉），
 *    因为它是唯一一种"用户没写错、但需要他补一个拼音"的情况。
 *
 * ## 为什么放在 shared
 *
 *   · 服务层在**预览与提交同一条代码路径**上算出各项计数（预览说 10 就是 10）；
 *   · 界面只负责**渲染** `describeImportCounts()` 的输出，不许自己再算一遍
 *     （两处各算一遍迟早漂移，而这张账的意义正是"数字可信"）。
 */

/** 一行的去向（服务层逐条判定后汇总出来）。 */
export interface ImportCountInput {
  /** 用户在框里粘的词元数（`ParseImportResult.stats.tokens`）。 */
  tokens: number;
  /** 解析器收下的数据行数（`ParseImportResult.dataRowCount`）。 */
  recognized: number;
  /** 因为词元违规而没收下的词元数（`ParseImportResult.stats.invalidTokens`）。 */
  problem: number;
  /** 解析出的字行里，有多少能落成"字"（`stats.charRows`）。 */
  charRows: number;
  /** 解析出的词行里，有多少能落成"词"（`stats.wordRows`）。 */
  wordRows: number;
  /** 本次新增的条数。 */
  created: number;
  /** 本次会更新的条数。 */
  updated: number;
  /** 已存在、跳过的条数。 */
  skipped: number;
  /** **同一份粘贴里重复**、被合并掉的条数（服务层的批内去重；**只有单读音的字/词**）。 */
  merged: number;
  /**
   * 同一份粘贴里**多音字**重复出现、因此只存 1 条的条数（**不算"重复录入"**）。
   *
   * ⚠️ 它与 `merged` 一起构成"份内重复"的全部：`merged + mergedPolyphonic` = 服务层
   *    去重时实际 `continue` 掉的条数。分开是为了让用户看清"哪几条是打错了、
   *    哪几条是这个字本来就多音"（见 `import-duplicates.ts` 的 `classifyImportDuplicate`）。
   */
  mergedPolyphonic: number;
  /**
   * 多音字**去重后**的条目数（= 重复明细里 `action === 'polyphonic'` 那组的长度）。
   * 界面上就是"**多音字有几个**"那个数，后面还要把它们的读音列出来。
   */
  polyphonicChars: number;
  /** 收下了但落不了库的条数（缺拼音）。 */
  unresolved: number;
  /** 用户勾的那张表（`null` = 通用导入，不分表）。 */
  listType: 'shizi' | 'xiezi' | 'cibiao' | null;
}

/** 预览里那张账的全部数字（与 `ImportCountInput` 一一对应，便于原样返回）。 */
export interface ImportCounts extends ImportCountInput {
  /**
   * **一共几个字/词（多音字只算一次）** —— 用户点名要的那个数（2026-10-17）。
   *
   * `= created + updated + skipped + unresolved`，也就是"去重之后实际会留在字表里的
   * 行数（含已经在库里的）"。⚠️ 它**不含** `merged` / `mergedPolyphonic`：
   * 那两档是被同一份内容里的重复"多出来"的行，字表里只剩 1 条。
   */
  distinctUnits: number;
  /**
   * 那两条恒等式是否都成立。
   *
   * ⚠️ 它是**给开发者/测试看的**，不是给家长看的：`false` 说明我们漏算或多算了，
   *    界面上**不要**把 `false` 渲染成一句"数字可能有误"糊过去 ——
   *    要么修好，要么在测试里说清为什么这一份数据天然不满足。
   */
  balanced: boolean;
}

/**
 * 把两边的计数拼成一张**能核对**的账。
 *
 * 这个函数刻意**不做任何猜测**：所有输入都由调用方从**真实产出**里取
 * （解析器的 `stats` / 服务层的逐条判定结果），它只做加法与两条恒等式的检查。
 */
export function summarizeImportCounts(input: ImportCountInput): ImportCounts {
  const distinctUnits = input.created + input.updated + input.skipped + input.unresolved;
  const balanced =
    input.tokens === input.recognized + input.problem &&
    input.recognized ===
      input.created +
        input.updated +
        input.skipped +
        input.merged +
        input.mergedPolyphonic +
        input.unresolved;
  return { ...input, distinctUnits, balanced };
}

/** 把数字渲染成人话（数字拿不到时显示「—」，**不显示 0**）。 */
function n(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return String(value);
}

/**
 * 「这次录的是哪张表」——一句话。
 *
 * 用户会**分别核对**"录了多少个字"和"录了多少个词"，所以要让他一眼看出这一份
 * 是哪种表：字表只该有字、词语表只该有词。
 */
export function describeImportScope(listType: ImportCountInput['listType']): string {
  if (listType === 'shizi') return '这次录的是「识字表」——只统计字';
  if (listType === 'xiezi') return '这次录的是「写字表」——只统计字';
  if (listType === 'cibiao') return '这次录的是「词语表」——只统计词语';
  return '这次是通用整块导入（字与词分开统计）';
}

/**
 * 只渲染那两句人话所需的最小字段（`ImportCounts` 与前端解码出来的
 * `ImportCountsView` 都满足它 —— 前端那份的 `listType` 是宽类型 `string | null`）。
 */
export interface ImportCountsDisplay {
  distinctUnits: number;
  polyphonicChars: number;
  listType: string | null;
}

/**
 * 「一共几个字/词」那一行的**量词**（按这次录的是哪张表说）。
 *
 * 用户原话（2026-10-17）："最后，还要注明，**一共有几个字（这里的统计，多音字只统计
 * 一次）**"。没选表类型时说"条目"，**不替用户猜**成"字"或"词"。
 */
export function distinctUnitLabel(listType: string | null): string {
  if (listType === 'shizi' || listType === 'xiezi') return '个字（多音字只算一次）';
  if (listType === 'cibiao') return '个词（重复的只算一次）';
  return '个条目（多音字只算一次）';
}

/**
 * **一共几个字（多音字只算一次）** —— 用户点名要的那一句。
 *
 * 这是"实际会留在字表里多少行"的口径：`created + updated + skipped + unresolved`。
 * ⚠️ 它**不是** `recognized`：同一份里重复的那几条（打错的 + 多音字的）在这里只算 1 次。
 */
export function describeDistinctTotal(counts: ImportCountsDisplay): string {
  return `一共 ${n(counts.distinctUnits)} ${distinctUnitLabel(counts.listType)}`;
}

/** **多音字有几个** —— 一句话（读音清单见 `describePolyphonicDuplicates()`）。 */
export function describePolyphonicCount(counts: ImportCountsDisplay): string {
  return `多音字（重复出现的）：${n(counts.polyphonicChars)} 个`;
}

/**
 * 那张账的**逐行文案**（界面直接按行渲染，窄屏上一行一项、不会挤成一团）。
 *
 * 形状（用户核对的就是这几行）：
 *
 * ```
 * 你在框里粘了：                100 个字元
 * 识别出来：                    99 个（字 99 / 词 0）
 *   ├ 本次新增：                 10 个
 *   ├ 之前已录过（会跳过）：      87 个
 *   ├ 会更新（拼音/收录/词语表）： 0 个
 *   ├ 份内重复·打错的（已合并）：  1 个
 *   ├ 多音字重复（只存 1 条）：    0 个
 *   └ 需要补拼音（这次没收）：     1 个
 * 一共 97 个字（多音字只算一次）
 * 多音字（重复出现的）：          0 个
 * 有问题、没收的：                1 个（下面逐条列出是哪一个）
 * ```
 *
 * ⚠️ 每一项都在上面那张账里**单独占一行**，因为它们**加起来正好等于**
 *    "识别出来"那个数 —— 用户拿计算器按一遍就能确认我们没漏算。
 * ⚠️ "一共几个字"与"多音字几个"**不在**那条加法链里：前者是去重后的口径，
 *    后者是 `mergedPolyphonic` 按字去重后的条数（同一个多音字出现 3 次时，
 *    `mergedPolyphonic` 是 2，`polyphonicChars` 是 1）。
 */
export function describeImportCounts(counts: ImportCounts): string[] {
  const lines: string[] = [];
  lines.push(`你在框里粘了：${n(counts.tokens)} 个字元`);
  lines.push(
    `识别出来：${n(counts.recognized)} 个（字 ${n(counts.charRows)} / 词 ${n(counts.wordRows)}）`,
  );
  lines.push(`　├ 本次新增：${n(counts.created)} 个`);
  lines.push(`　├ 之前已录过（会跳过）：${n(counts.skipped)} 个`);
  lines.push(`　├ 会更新（拼音/收录/词语表）：${n(counts.updated)} 个`);
  lines.push(`　├ 份内重复·打错的（已合并）：${n(counts.merged)} 个`);
  lines.push(`　├ 多音字重复（只存 1 条）：${n(counts.mergedPolyphonic)} 个`);
  lines.push(`　└ 需要补拼音（这次没收）：${n(counts.unresolved)} 个`);
  lines.push(describeDistinctTotal(counts));
  lines.push(describePolyphonicCount(counts));
  lines.push(`有问题、没收的：${n(counts.problem)} 个（下面逐条列出是哪一个）`);
  lines.push(describeImportScope(counts.listType));
  return lines;
}
